import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Pagination from "../Layout/Pagination.jsx";
import MemberAlarm from "../Modals/MemberAlarm.jsx";
import MemberCheck from "../Modals/MemberCheck.jsx";
import MemberManageActionBar from "./Members.manageActionBar.jsx";
import MemberManageFilters from "./Members.manageFilters.jsx";
import MemberRow from "./Members.row.jsx";
import {
  buildMemberColumns,
  getMemberGridTemplate,
  getMemberTableMinWidth,
} from "./Members.tableConfig.js";
import { postJson } from "../Utils/apiUtils.js";
import { getCompanyProfile } from "../Utils/sessionUtils.js";

const MEMBER_TABLE_MIN_WIDTH = "1500px";

const MemberManage = ({
  memberData_list,
  total_member,
  setSearchWord,
  departmentList,
  setDepartment,
  setSort,
  setNation,
  getMemberList,
  refreshMemberList,
  maskMemberEmail,
  page,
  setPage,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  handleDownloadExcel,
  setStatus,
  handleOpenDepartmentModal,
}) => {
  const { companyCode } = getCompanyProfile();
  const showSecretCode = companyCode === "citibank";
  const canManageDepartmentAndRelation =
    companyCode === "citibank" ||
    companyCode === "test" ||
    companyCode === "runpoint";
  const navigate = useNavigate();

  const [clickActive, setClickActive] = useState(true);
  const [clickAndEdit, setClickAndEdit] = useState(false);
  const [clickMemberActive, setClickMemberActive] = useState(false);
  const [clickMemberInactive, setClickMemberInactive] = useState(false);
  const [clickMemberLeave, setClickMemberLeave] = useState(false);
  const [userIdArr, setUserIdArr] = useState([]);
  const [alarmState, setAlarmState] = useState({
    show: false,
    title: "",
    description: "",
  });
  const [clickInput, setClickInput] = useState(false);
  const [openMoreUserId, setOpenMoreUserId] = useState(null);
  const [editingMemberState, setEditingMemberState] = useState(null);

  const updateActive = async () => {
    try {
      const body = {
        userIdArr: userIdArr,
      };
      const res = await postJson("/dashboard/user/updateActive", body, navigate);
      if (res.code === 200) {
        setAlarmState({
          show: true,
          title: "구성원 활성화 완료",
          description: "선택한 구성원이 활성화 되었습니다.",
        });
      } else if (res.code === 1011) {
        setAlarmState({
          show: true,
          title: "구성원 활성화 실패",
          description: "구성원 용량이 충분한지 확인해주세요. ",
        });
      }
    } catch (error) {
      console.error("구성원 활성화 중 에러:", error);
    }
  };

  const updateInActive = async () => {
    try {
      const body = {
        userIdArr: userIdArr,
      };
      const res = await postJson("/dashboard/user/updateInActive", body, navigate);
      if (res.code === 200) {
        setAlarmState({
          show: true,
          title: "구성원 비활성화 완료",
          description: "선택한 구성원이 비활성화 되었습니다.",
        });
        setClickMemberInactive(false);
      } else if (res.code === 1011) {
        setAlarmState({
          show: true,
          title: "구성원 비활성화 실패",
          description: "다시 한 번 시도해주세요.",
        });
        setClickMemberInactive(false);
      }
    } catch (error) {
      console.error("구성원 활성화 중 에러:", error);
    }
  };

  const deleteMember = async () => {
    try {
      const body = {
        userIdArr: userIdArr,
      };
      const data = await postJson("/dashboard/user/delete", body, navigate);

      if (data.code === 200) {
        setAlarmState({
          show: true,
          title: "구성원 탈퇴 완료",
          description: "선택한 구성원이 탈퇴되었습니다.",
        });
        setClickMemberLeave(false);
      } else if (data.code === 1011) {
        setAlarmState({
          show: true,
          title: "구성원 탈퇴 실패",
          description: "하루 최대 10명까지 탈퇴시킬 수 있습니다.",
        });
        setClickMemberLeave(false);
      }
    } catch (error) {
      console.error("기업회원 탈퇴 중 에러: ", error);
    }
  };

  const selectAll = () => {
    memberData_list.slice(0, 10).map((data) =>
      setUserIdArr((prev) =>
        userIdArr.includes(data.userId) ? [...prev] : [...prev, data.userId]
      )
    );
  };

  const removeAll = () => {
    memberData_list.slice(0, 10).map((data) =>
      setUserIdArr((prev) => prev.filter((item) => item !== data.userId))
    );
  };

  const dropDownDepartmentList = departmentList.map(
    (row) => row.departmentName
  );
  const hasEditingMember = Boolean(editingMemberState);
  const memberColumns = useMemo(
    () =>
      buildMemberColumns({
        clickAndEdit,
        canManageDepartmentAndRelation,
        hasEditingMember,
        showSecretCode,
      }),
    [clickAndEdit, canManageDepartmentAndRelation, hasEditingMember, showSecretCode]
  );
  const memberGridTemplate = useMemo(
    () => getMemberGridTemplate(memberColumns),
    [memberColumns]
  );
  const memberTableMinWidth = useMemo(
    () => getMemberTableMinWidth(memberColumns),
    [memberColumns]
  );

  const renderMemberHeaderCell = (column) => {
    if (column.type === "select") {
      return (
        <div key={column.key} className="center">
          <HeaderSelectCheckbox
            type="checkbox"
            onChange={(e) => (e.target.checked ? selectAll() : removeAll())}
          />
        </div>
      );
    }

    if (column.type === "actions") {
      return <div key={column.key} />;
    }

    return (
      <p key={column.key} className={column.align}>
        {column.label}
      </p>
    );
  };

  const handleStartEdit = (member) => {
    setOpenMoreUserId(null);
    setEditingMemberState({
      userId: member.userId,
      editDepartmentId: String(member.companyDepartmentId ?? "__none__"),
      editMemberType: String(member.memberType ?? 1),
    });
  };

  const handleFinishEdit = () => {
    setEditingMemberState(null);
  };

  const handleEditDepartmentChange = (value) => {
    setEditingMemberState((prev) =>
      prev ? { ...prev, editDepartmentId: value } : prev
    );
  };

  const handleEditMemberTypeChange = (value) => {
    setEditingMemberState((prev) =>
      prev ? { ...prev, editMemberType: value } : prev
    );
  };

  const isExcelDownloadDisabled = total_member === 0;

  const showNoSelectedMemberAlarm = () => {
    setAlarmState({
      show: true,
      title: "선택한 구성원이 없습니다",
      description: "구성원을 선택해주세요.",
    });
  };

  const showEditingMemberAlarm = () => {
    setAlarmState({
      show: true,
      title: "구성원 편집 중입니다",
      description: "현재 편집을 종료한 뒤 선택 편집을 진행해주세요.",
    });
  };

  const handleStartSelectionEdit = () => {
    if (editingMemberState) {
      showEditingMemberAlarm();
      return;
    }

    setOpenMoreUserId(null);
    setClickAndEdit(true);
  };

  const runWithSelectedMembers = (action) => {
    if (userIdArr.length === 0) {
      showNoSelectedMemberAlarm();
      return;
    }

    action();
  };

  const handleBulkDeactivate = () => {
    runWithSelectedMembers(() => setClickMemberInactive(true));
  };

  const handleBulkDelete = () => {
    runWithSelectedMembers(() => setClickMemberLeave(true));
  };

  const handleBulkActivate = () => {
    runWithSelectedMembers(() => setClickMemberActive(true));
  };

  useEffect(() => {
    if (!editingMemberState) {
      return;
    }

    const isEditingMemberVisible = memberData_list.some(
      (member) => member.userId === editingMemberState.userId
    );

    if (!isEditingMemberVisible) {
      setEditingMemberState(null);
      setOpenMoreUserId(null);
    }
  }, [editingMemberState, memberData_list]);

  useEffect(() => {
    const visibleUserIds = new Set(memberData_list.map((member) => member.userId));

    setUserIdArr((prev) => prev.filter((userId) => visibleUserIds.has(userId)));
  }, [memberData_list]);

  return (
    <div>
      {clickMemberActive && (
        <MemberCheck
          type="활성화"
          handleApply={updateActive}
          handleClickX={() => setClickMemberActive(false)}
        />
      )}
      {clickMemberInactive && (
        <MemberCheck
          type="비활성화"
          handleApply={updateInActive}
          handleClickX={() => setClickMemberInactive(false)}
        />
      )}
      {clickMemberLeave && (
        <MemberCheck
          type="탈퇴"
          handleApply={deleteMember}
          handleClickX={() => setClickMemberLeave(false)}
        />
      )}
      {alarmState.show && (
        <MemberAlarm
          title={alarmState.title}
          description={alarmState.description}
          handleClickX={() =>
            setAlarmState({ show: false, title: "", description: "" })
          }
        />
      )}

      <MemberManageFilters
        clickActive={clickActive}
        clickInput={clickInput}
        canManageDepartmentAndRelation={canManageDepartmentAndRelation}
        dropDownDepartmentList={dropDownDepartmentList}
        startTime={startTime}
        endTime={endTime}
        onSetStatus={setStatus}
        onSetClickActive={setClickActive}
        onSetSearchWord={setSearchWord}
        onSetClickInput={setClickInput}
        onSearch={getMemberList}
        onSetDepartment={setDepartment}
        onSetSort={setSort}
        onSetNation={setNation}
        onSetStartTime={setStartTime}
        onSetEndTime={setEndTime}
      />
      <MemberManageActionBar
        totalMember={total_member}
        clickAndEdit={clickAndEdit}
        userIdCount={userIdArr.length}
        isExcelDownloadDisabled={isExcelDownloadDisabled}
        canManageDepartmentAndRelation={canManageDepartmentAndRelation}
        clickActive={clickActive}
        onDownloadExcel={handleDownloadExcel}
        onOpenDepartmentModal={handleOpenDepartmentModal}
        onStartSelectionEdit={handleStartSelectionEdit}
        onCancelSelectionEdit={() => setClickAndEdit(false)}
        onDeactivateSelected={handleBulkDeactivate}
        onDeleteSelected={handleBulkDelete}
        onActivateSelected={handleBulkActivate}
      />

      <TableScrollArea>
        <MemberTableLayout $minWidth={memberTableMinWidth}>
          <MemberTableHeader
            $gridTemplate={memberGridTemplate}
            $minWidth={memberTableMinWidth}
          >
            {memberColumns.map(renderMemberHeaderCell)}
          </MemberTableHeader>
          {memberData_list.map((data, index) => (
            <MemberRow
              key={data.userId}
              userId={data.userId}
              ranking={data.ranking}
              secretCode={data.secretCode}
              departmentName={data.departmentName}
              rownum={total_member - index}
              index={index}
              page={page}
              offset={10}
              nickName={
                data.nickName
              }
              email={maskMemberEmail ? "-" : data.email}
              totalStep={data.totalStep}
              prevMonthStep={data.prevMonthStep}
              currentMonthStep={data.currentMonthStep}
              regTime={data.regTime}
              cCode={data.cCode}
              memberType={data.memberType}
              memberColumns={memberColumns}
              editingMemberState={editingMemberState}
              departmentList={departmentList}
              clickActive={clickActive}
              setUserIdArr={setUserIdArr}
              userIdArr={userIdArr}
              isMoreOpen={openMoreUserId === data.userId}
              onToggleMore={() =>
                setOpenMoreUserId((prev) =>
                  prev === data.userId ? null : data.userId
                )
              }
              onCloseMore={() => setOpenMoreUserId(null)}
              isEditing={editingMemberState?.userId === data.userId}
              canManageMemberInfo={canManageDepartmentAndRelation}
              onStartEdit={() => handleStartEdit(data)}
              onFinishEdit={handleFinishEdit}
              onMemberUpdated={refreshMemberList}
              onEditDepartmentChange={handleEditDepartmentChange}
              onEditMemberTypeChange={handleEditMemberTypeChange}
              isActionColumnCompact={!hasEditingMember}
              memberGridTemplate={memberGridTemplate}
              memberTableMinWidth={memberTableMinWidth}
            />
          ))}
        </MemberTableLayout>
      </TableScrollArea>

      <Pagination
        totalPages={total_member === 0 ? 1 : Math.ceil(total_member / 10)}
        currentPage={page}
        onPageChange={(pageNum) => setPage(pageNum)}
      />
    </div>
  );
};

const TableScrollArea = styled.div`
  width: 100%;
  margin-top: 20px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: auto;
  scrollbar-color: #b8c0cc #edf1f5;
  &::-webkit-scrollbar {
    height: 12px;
  }
  &::-webkit-scrollbar-track {
    background-color: #edf1f5;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #b8c0cc;
    border-radius: 999px;
  }
`;

const MemberTableLayout = styled.div.attrs({ className: "listArea" })`
  width: max-content;
  min-width: ${(props) => props.$minWidth ?? MEMBER_TABLE_MIN_WIDTH};
`;

const MemberTableHeader = styled.div.attrs({ className: "list_titleBar" })`
  width: max-content;
  min-width: ${(props) => props.$minWidth ?? MEMBER_TABLE_MIN_WIDTH};
  grid-template-columns: ${(props) => props.$gridTemplate};
  > p {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HeaderSelectCheckbox = styled.input`
  width: 20px;
  height: 20px;
`;

export default MemberManage;
